import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { formatDate, formatMoney } from '../lib/format'
import { suggestPenalty, daysLate, PENALTY_RATE_PER_DAY } from '../lib/rules'
import { canApprove, canEnterData } from '../lib/types'
import type { Payment, RentStatusRow } from '../lib/types'
import { Sheet } from '../components/Sheet'
import {
  MoneyField, DateField, SelectField, TextField, TextAreaField,
} from '../components/Fields'
import { DocumentLink } from '../components/DocumentViewer'
import type { PaymentMethod } from '../lib/types'

/**
 * Detail d'un loyer du mois : ce qui est du, tous les versements deja
 * enregistres, et la saisie d'un nouveau versement.
 *
 * Plusieurs versements par loyer, c'est ainsi que les paiements
 * partiels fonctionnent : on n'ecrase jamais un versement, on en
 * ajoute un.
 */
export function ChargeSheet({
  row,
  onClose,
  onSaved,
}: {
  row: RentStatusRow
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useI18n()
  const { profile } = useAuth()

  const [amount, setAmount] = useState<number | null>(
    row.balance > 0 ? row.balance : null,
  )
  const [paidOn, setPaidOn] = useState(new Date().toISOString().slice(0, 10))
  const [method, setMethod] = useState<PaymentMethod>('especes')
  const [receiptNumber, setReceiptNumber] = useState('')
  const [notes, setNotes] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const peutSaisir = canEnterData(profile?.role ?? null)
  const peutApprouver = canApprove(profile?.role ?? null)

  const { data: payments, reload } = useAsync(
    async () => {
      const { data, error: e } = await supabase
        .from('payments')
        .select(
          'id, charge_id, period, paid_on, amount, method,' +
            ' receipt_number, receipt_document, notes',
        )
        .eq('charge_id', row.charge_id)
        .order('paid_on', { ascending: true })
      if (e) throw e
      return (data ?? []) as unknown as Payment[]
    },
    [row.charge_id],
  )

  const penalty = suggestPenalty(row.period, row.balance, row.last_payment_on)

  async function submit() {
    if (amount === null || amount <= 0) return
    setError(null)
    setSubmitting(true)

    const { error: e } = await supabase.from('payments').insert({
      charge_id: row.charge_id,
      // period est recopiee depuis la charge par un declencheur, mais
      // la colonne est NOT NULL : on l'envoie aussi.
      period: row.period,
      paid_on: paidOn,
      amount,
      method,
      receipt_number: receiptNumber.trim() || null,
      // Plus de recu par locataire : la preuve est le bordereau de
      // versement mensuel, joint dans l'ecran Banque.
      receipt_document: null,
      notes: notes.trim() || null,
    })

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  /** Applique ou annule la penalite. Proprietaire uniquement. */
  async function decidePenalty(apply: boolean) {
    setError(null)
    setSubmitting(true)
    const { data: userData } = await supabase.auth.getUser()

    const { error: e } = await supabase
      .from('rent_charges')
      .update({
        penalty_suggested: penalty?.amount ?? null,
        penalty_applied: apply ? (penalty?.amount ?? null) : null,
        penalty_status: apply ? 'applique' : 'annule',
        penalty_decided_by: userData.user?.id ?? null,
        penalty_decided_at: new Date().toISOString(),
      })
      .eq('id', row.charge_id)

    setSubmitting(false)
    if (e) {
      setError(`${t('save_failed')} ${e.message}`)
      return
    }
    onSaved()
  }

  return (
    <Sheet
      title={row.unit_name}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('save')}
      submitting={submitting}
      disabled={!peutSaisir || amount === null || amount <= 0}
      error={error}
    >
      {/* Etat du loyer, en clair et verifiable. */}
      <dl className="deflist">
        <dt>{t('lease_tenant')}</dt>
        <dd>{row.tenant_name}</dd>
        <dt>{t('rent_expected')}</dt>
        <dd>{formatMoney(row.amount_due)}</dd>
        <dt>{t('rent_received')}</dt>
        <dd>{formatMoney(row.amount_received)}</dd>
        <dt>{t('rent_balance')}</dt>
        <dd>
          <strong>{formatMoney(row.balance)}</strong>
        </dd>
        <dt>{t('rent_due_on', { date: '' }).replace('{date}', '').trim()}</dt>
        <dd>{formatDate(row.due_on)}</dd>
      </dl>

      {row.covered_by_advance && (
        <div className="banner banner--warn">
          <strong>{t('rent_covered_advance')}</strong>
        </div>
      )}

      {/* Penalite de retard : une suggestion, jamais une application
          automatique. */}
      {penalty && (
        <div className="banner banner--error">
          <strong>{t('penalty_title')}</strong>
          <span>{formatMoney(penalty.amount)}</span>
          <span>
            {t('penalty_detail', {
              days: penalty.daysLate,
              rate: PENALTY_RATE_PER_DAY * 100,
              base: formatMoney(penalty.base),
            })}
          </span>
          <span>{t('penalty_unconfirmed')}</span>
          {peutApprouver ? (
            <div className="banner__actions">
              <button
                type="button"
                className="btn btn--primary btn--small"
                disabled={submitting}
                onClick={() => void decidePenalty(true)}
              >
                {t('penalty_apply')}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={submitting}
                onClick={() => void decidePenalty(false)}
              >
                {t('penalty_waive')}
              </button>
            </div>
          ) : (
            <span>{t('penalty_owner_only')}</span>
          )}
        </div>
      )}

      {/* Versements deja enregistres. */}
      <h3 className="screen__section">{t('payments_list')}</h3>
      {(payments ?? []).length === 0 ? (
        <p className="muted">{t('payments_none')}</p>
      ) : (
        <ul className="minilist">
          {(payments ?? []).map((p) => {
            const retard = daysLate(p.period, p.paid_on)
            return (
              <li key={p.id} className="minilist__item">
                <span>
                  <strong>{formatMoney(p.amount)}</strong>
                  <span className="list__meta"> {formatDate(p.paid_on)}</span>
                </span>
                <span className="list__side">
                  <span className="list__meta">
                    {t(`payment_method_${p.method}` as
                      'payment_method_especes' | 'payment_method_wave'
                      | 'payment_method_virement' | 'payment_method_cheque')}
                  </span>
                  <span className={`chip ${retard > 0 ? 'chip--warn' : 'chip--ok'}`}>
                    {retard > 0
                      ? t('payment_late_by', { n: retard })
                      : t('payment_on_time')}
                  </span>
                  {/* Le recu de ce versement, consultable ici meme. */}
                  <DocumentLink documentId={p.receipt_document} />
                </span>
              </li>
            )
          })}
        </ul>
      )}

      {/* Nouveau versement. */}
      {peutSaisir && (
        <>
          <h3 className="screen__section">{t('payment_add')}</h3>

          <MoneyField
            label={t('payment_amount')}
            required
            value={amount}
            onChange={setAmount}
          />
          <DateField
            label={t('payment_date')}
            required
            value={paidOn}
            onChange={setPaidOn}
          />
          <SelectField<PaymentMethod>
            label={t('payment_method')}
            required
            value={method}
            onChange={setMethod}
            options={[
              { value: 'especes', label: t('payment_method_especes') },
              { value: 'wave', label: t('payment_method_wave') },
              { value: 'virement', label: t('payment_method_virement') },
              { value: 'cheque', label: t('payment_method_cheque') },
            ]}
          />
          <TextField
            label={t('payment_receipt_number')}
            hint={t('optional')}
            value={receiptNumber}
            onChange={setReceiptNumber}
            autoCapitalize="none"
          />

          {/* PAS DE PHOTO ICI, VOLONTAIREMENT.
              Les loyers sont encaisses puis verses en banque en une
              seule fois par mois : la piece justificative est le
              bordereau de ce versement, un par mois, et non un recu par
              locataire. Elle se joint donc a l'ecran Banque. */}
          <p className="field__hint">{t('payment_proof_at_bank')}</p>

          <TextAreaField
            label={t('payment_notes')}
            hint={t('optional')}
            value={notes}
            onChange={setNotes}
          />
        </>
      )}

      <button
        type="button"
        className="btn btn--ghost btn--small"
        onClick={reload}
        hidden
      >
        {t('retry')}
      </button>
    </Sheet>
  )
}
